import { format } from 'date-fns';

export const formatTimeToIST = (timestamp) => {
  if (!timestamp) return '';

  try {
    // If it's an ISO string (e.g. "2026-06-30T15:32:05.251802Z"), we want to show 
    // the exact time we get in the string without adding 5:30 hrs timezone offset.
    if (typeof timestamp === 'string' && timestamp.includes('T')) {
      // Parse as UTC to prevent timezone offset conversion
      return new Date(timestamp).toLocaleTimeString('en-IN', {
        timeZone: 'UTC',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }

    const timestampInMs = timestamp.toString().length === 10 ? timestamp * 1000 : timestamp;
    
    return new Date(timestampInMs).toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',  // Convert to IST
      hour12: false,  // 24-hour format
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch (err) {
    console.error('Error formatting time to IST:', err);
    return timestamp?.toString() || '';  // Return original value if parsing fails
  }
};

// Date counterpart of formatTimeToIST (dd-MM-yyyy, same zone), so a date and its
// time always describe the same instant, even for viewers outside India.
export const formatDateToIST = (timestamp) => {
  if (!timestamp) return '';

  try {
    // ISO strings carry the wall-clock time as-is (see formatTimeToIST).
    const isWallClockIso = typeof timestamp === 'string' && timestamp.includes('T');
    let date;
    if (isWallClockIso) {
      date = new Date(timestamp);
    } else {
      const value = Number(timestamp);
      date = new Date(String(Math.trunc(value)).length === 10 ? value * 1000 : value);
    }
    if (isNaN(date.getTime())) return '';

    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: isWallClockIso ? 'UTC' : 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).formatToParts(date);
    const part = (type) => parts.find((p) => p.type === type)?.value;
    return `${part('day')}-${part('month')}-${part('year')}`;
  } catch (err) {
    console.error('Error formatting date to IST:', err);
    return '';
  }
};

// Shared by the Reports tabs so every tab sends and shows dates identically.
// Both parse in LOCAL time, matching how the date pickers produce values.
export const formatApiDate = (date) => {
  if (!date) return '';
  const dateObj = date instanceof Date ? date : new Date(date);
  return isNaN(dateObj.getTime()) ? '' : format(dateObj, 'yyyy-MM-dd');
};

// Reports APIs send IST wall-clock ISO strings despite the trailing `Z`, so the
// UTC parts are exactly what was sent (no +5:30). For a real Unix timestamp use
// formatDateToIST instead.
export const formatDateAsSent = (value) => {
  if (!value) return '';
  const dateObj = value instanceof Date ? value : new Date(value);
  if (isNaN(dateObj.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(dateObj.getUTCDate())}-${pad(dateObj.getUTCMonth() + 1)}-${dateObj.getUTCFullYear()}`;
};

// "19-10-2026 15:29:43", both parts read as sent (see formatDateAsSent).
export const formatDateTimeAsSent = (value) => {
  const date = formatDateAsSent(value);
  return date ? `${date} ${formatTimeToIST(value)}` : '';
};
